/**
 * monetization/providers — provider-agnostic payments abstraction (client side).
 *
 * The client NEVER talks to the gateway directly: the server exposes
 * POST /api/billing/checkout and the provider injected there decides (Rust trait
 * `PaymentProvider`). This TS mirror describes how the UI should INTERPRET the
 * active provider: whether it offers automatic checkout or whether the upgrade is a
 * manual flow (provider "none": applied by an admin).
 *
 * Adding a new provider = registering its descriptor here + implementing the
 * trait in Rust. No screen or domain logic changes.
 */

import {
  clampProviderToPlatform,
  isStoreBinaryPlatform,
  normalizePlatform,
  type PurchaseGateReason,
  type ResolvedPurchasePlatform,
  UNKNOWN_PURCHASE_PLATFORM,
} from "./purchase";

export interface CheckoutResult {
  provider: string;
  /** Hosted payment URL to redirect to (if the provider offers one). */
  checkoutUrl: string | null;
  /** true ⇒ manual upgrade (contact / applied by an admin). */
  manual: boolean;
}

/** Descriptor of a payment provider, as seen from the UI. */
export interface PaymentProviderDescriptor {
  id: string;
  /** Does the upgrade open a hosted checkout? (false ⇒ manual flow). */
  supportsCheckout: boolean;
  /** Does the server receive webhooks from this provider? */
  supportsWebhooks: boolean;
}

const NONE_PROVIDER: PaymentProviderDescriptor = {
  id: "none",
  supportsCheckout: false,
  supportsWebhooks: false,
};

const REGISTRY: Record<string, PaymentProviderDescriptor> = {
  none: NONE_PROVIDER,
  stripe: { id: "stripe", supportsCheckout: true, supportsWebhooks: true },
};

/** Registered ids (for the admin panel's Select). */
export const PAYMENT_PROVIDER_IDS = Object.keys(REGISTRY);

/** Descriptor of the active provider; unknown ⇒ behaves as "none". */
export function getPaymentProvider(id: string | undefined): PaymentProviderDescriptor {
  return REGISTRY[id ?? "none"] ?? NONE_PROVIDER;
}

/**
 * Interprets the server's checkout response in a provider-agnostic way:
 * returns the action the UI must execute.
 *
 * `blocked` ⇒ **navigates nowhere**: the surface doesn't allow that
 * purchase (see `monetization/purchase.ts`).
 */
export type CheckoutAction =
  | { kind: "redirect"; url: string }
  | { kind: "manual" }
  | { kind: "blocked"; reason: PurchaseGateReason };

/**
 * **The client's second gate, and the one that actually closes.**
 *
 * `platform` is a REQUIRED parameter on purpose. The previous version of
 * this function didn't have it and returned `redirect` with whatever URL arrived
 * from the server; the plan screen's `location.assign` did the rest.
 * Making it required turns forgetting it into a **type error** in every
 * clone: there's no need to remember the guard, it doesn't compile without it. (And it's
 * deliberately a type and not a lint: in this fleet the guards that only
 * looked at text weren't watching anything.)
 *
 * Rules, all fail-closed:
 *   * unclassified surface ⇒ `blocked`;
 *   * provider returned by the server that isn't legal on this surface
 *     (Stripe on the APK) ⇒ `blocked`;
 *   * **store binary ⇒ never navigate to a payment URL**, not even
 *     with a legal provider: Play policy also goes after
 *     *link-out*;
 *   * if there's no URL or the flow is manual ⇒ `manual`, as always.
 */
export function interpretCheckout(
  result: CheckoutResult,
  platform: ResolvedPurchasePlatform | string,
): CheckoutAction {
  const surface = normalizePlatform(platform);
  if (surface === UNKNOWN_PURCHASE_PLATFORM) {
    return { kind: "blocked", reason: "unknown-platform" };
  }
  const provider = result.provider.trim().toLowerCase();
  if (clampProviderToPlatform(surface, provider) !== provider) {
    return { kind: "blocked", reason: "provider-not-allowed" };
  }
  if (!result.manual && result.checkoutUrl) {
    if (isStoreBinaryPlatform(surface)) {
      // Belt and suspenders: even if a provider with a URL were ever legal
      // on a store binary, the outbound navigation is not.
      return { kind: "blocked", reason: "provider-not-allowed" };
    }
    return { kind: "redirect", url: result.checkoutUrl };
  }
  return { kind: "manual" };
}
